import { useState, useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "layout/Sidenav";
import theme from "assets/theme";
import allRoutes from "routes";
import { useDigitalPassController, setMiniSidenav } from "context";
import brandColor from "assets/images/icon.png";
import LoadingSuspense from "routes/LoadingSuspense";

// Lazy load components
const SignIn = lazy(() => import("components/Auth/SignIn"));
const SignInOTP = lazy(() => import("components/Auth/SignIn/SignInOTP"));
const VerificationSuccess = lazy(() => import("components/Auth/EmailVerification"));
const CompanyRegistrationCover = lazy(() => import("components/Auth/CompanyRegistration"));
const Logout = lazy(() => import("components/Auth/Logout"));
const ForgotPassword = lazy(() => import("components/Auth/ChangePassword/ForgotPassword"));
const ResetPassword = lazy(() => import("components/Auth/ChangePassword/ResetPassword"));
const ResetPasswordOTP = lazy(() => import("components/Auth/ChangePassword/ResetPasswordOTP"));
const UpdateMobileNumber = lazy(() =>
  import("components/Auth/ChangePassword/UpdateMobileNumber")
);
const PaymentStatus = lazy(() => import("components/Dashboard/Transactions/PaymentStatus"));

export default function App() {
  const [controller, dispatch] = useDigitalPassController();
  const { miniSidenav, sidenavColor, layout, companyDetails, usertype, userDetails } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (currentRoutes) =>
    currentRoutes.map((route) => {
      if (
        (usertype === 1 && (route.status <= companyDetails.status || route.status === -10)) ||
        usertype !== 1
      ) {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }
        if (route.type === "redirect") {
          return (
            <Route
              exact
              path={route.from}
              element={<Navigate to={route.to} replace />}
              key={route.key}
            />
          );
        }
        if (route.route) {
          return <Route exact path={route.route} element={route.component} key={route.key} />;
        }
      }
      return null;
    });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <Sidenav
          color={sidenavColor}
          brand={brandColor}
          brandName="Digital Portpass"
          routes={
            usertype === 0 && userDetails?.subType === "terminal_port_police"
              ? allRoutes.terminalPortPoliceRoutes
              : usertype === 0
              ? allRoutes.portPoliceRoutes
              : usertype === 1
              ? allRoutes.companyAdminRoutes
              : usertype === 3
              ? allRoutes.superAdminRoutes
              : usertype === 4
              ? allRoutes.eoaRoutes
              : usertype === 5
              ? allRoutes.eouRoutes
              : usertype === 6
              ? allRoutes.ppcAdminRoutes
              : allRoutes.userRoutes
          }
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      <Suspense fallback={<LoadingSuspense />}>
        <Routes>
          {usertype === 0 && userDetails?.subType === "terminal_port_police" && getRoutes(allRoutes.terminalPortPoliceRoutes)}
          {usertype === 0 && userDetails?.subType !== "terminal_port_police" && getRoutes(allRoutes.portPoliceRoutes)}
          {usertype === 1 && getRoutes(allRoutes.companyAdminRoutes)}
          {usertype === 2 && getRoutes(allRoutes.userRoutes)}
          {usertype === 3 && getRoutes(allRoutes.superAdminRoutes)}
          {usertype === 4 && getRoutes(allRoutes.eoaRoutes)}
          {usertype === 5 && getRoutes(allRoutes.eouRoutes)}
          {usertype === 6 && getRoutes(allRoutes.ppcAdminRoutes)}

          <Route path="/auth/companyregistration" element={<CompanyRegistrationCover />} />
          <Route path="/auth/signin" element={<SignIn />} />
          <Route path="/auth/signinOTP" element={<SignInOTP />} />
          <Route path="/auth/forgotPassword" element={<ForgotPassword />} />
          <Route path="/auth/resetPassword" element={<ResetPassword />} />
          <Route path="/auth/resetPasswordOTP" element={<ResetPasswordOTP />} />
          <Route path="/auth/updateMobileNumber" element={<UpdateMobileNumber />} />
          <Route path="/auth/logout" element={<Logout />} />
          <Route path="/auth/emailverification" element={<VerificationSuccess />} />
          <Route path="/transaction/status" element={<PaymentStatus />} />
          <Route path="*" element={<Navigate to="/auth/signin" />} />
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}
